// src/pages/Farmer/ViewFarmer.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import farmerService from '../../services/farmer.service';
import Toast from '../../components/Toast/Toast';
import '../../styles/AddPages.css';

const ViewFarmer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [farmer, setFarmer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const fetchFarmer = async () => {
      try {
        const response = await farmerService.getFarmerById(id);
        if (response.success) setFarmer(response.data);
      } catch (error) {
        setToast({
          message: error.response?.data?.message || 'Failed to fetch farmer details',
          type: 'error',
        });
      } finally {
        setLoading(false);
      }
    };
    fetchFarmer();
  }, [id]);

  if (loading) {
    return (
      <div className="add-farmer-page">
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div className="spinner"></div>
          <p>Loading farmer details...</p>
        </div>
      </div>
    );
  }

  if (!farmer) {
    return (
      <div className="add-farmer-page">
        <p>Farmer not found.</p>
        <button className="btn btn-secondary" onClick={() => navigate('/farmer/view')}>
          Back to Farmers
        </button>
      </div>
    );
  }

  const vm = farmer.villageMaster || {};
  const villageDisplay = vm.name
    ? `${vm.name}${vm.villageCode ? ` (${vm.villageCode})` : ''}`
    : farmer.village;   // legacy fallback

  const DetailRow = ({ label, value }) => (
    <div className="form-group">
      <label className="form-label" style={{ fontWeight: 600, color: '#555' }}>{label}</label>
      <div className="form-input" style={{
        background: '#f8f9fa', color: '#333', cursor: 'default',
        minHeight: 42, display: 'flex', alignItems: 'center',
      }}>
        {value || <span style={{ color: '#aaa' }}>—</span>}
      </div>
    </div>
  );

  return (
    <div className="add-farmer-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><a href="/dashboard/overview">Dashboard</a></li>
          <li className="breadcrumb-item"><a href="/farmer/view">Farmers</a></li>
          <li className="breadcrumb-item active">View Farmer</li>
        </ol>
      </nav>

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h4 className="page-title">Farmer Details</h4>
        <button className="btn btn-primary" onClick={() => navigate(`/farmer/edit/${id}`)}>
          ✏️ Edit Farmer
        </button>
      </div>

      <div className="form-card">
        {/* Basic info */}
        <div className="form-section">
          <h3 className="section-title">👤 Farmer Info</h3>
          <div className="form-row">
            <DetailRow label="Farmer Name"  value={farmer.farmerName} />
            <DetailRow label="Mobile No"    value={farmer.mobileNo} />
            <DetailRow label="Headquarter"  value={farmer.headquarter?.name} />
          </div>
        </div>

        {/* Location */}
        <div className="form-section">
          <h3 className="section-title">📍 Location</h3>
          <div className="form-row">
            <DetailRow label="State"    value={vm.stateName} />
            <DetailRow label="District" value={vm.districtName} />
            <DetailRow label="Tehsil"   value={vm.talukaName} />
          </div>
          <div className="form-row">
            <DetailRow label="Village" value={villageDisplay} />
          </div>
        </div>

        {/* Farm details */}
        <div className="form-section">
          <h3 className="section-title">🌾 Farm Details</h3>
          <div className="form-row">
            <DetailRow label="Crop" value={farmer.crop} />
            <DetailRow label="Acre" value={farmer.acre} />
            <DetailRow label="Date"
              value={farmer.currentDate
                ? new Date(farmer.currentDate).toLocaleDateString('en-IN')
                : null}/>
          </div>
          <div className="form-row">
            <DetailRow label="Latitude"  value={farmer.latitude} />
            <DetailRow label="Longitude" value={farmer.longitude} />
            <DetailRow label="Traveling With"
              value={
                farmer.travelingWithEmployee
                  ? `${farmer.travelingWithEmployee.firstName} ${farmer.travelingWithEmployee.lastName}`
                  : farmer.travelingWith || null
              }/>
          </div>
        </div>

        {/* Map preview */}
        {farmer.latitude && farmer.longitude && (
          <div style={{ marginTop: 16 }}>
            <label className="form-label" style={{ fontWeight: 600, color: '#555' }}>Location Map</label>
            <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
              <iframe
                title="Farmer Location"
                width="100%" height="250" style={{ border: 'none', display: 'block' }}
                src={`https://maps.google.com/maps?q=${farmer.latitude},${farmer.longitude}&z=14&output=embed`}
              />
            </div>
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="btn btn-secondary"
            onClick={() => navigate('/farmer/view')}>
            ← Back to List
          </button>
          <button type="button" className="btn btn-primary"
            onClick={() => navigate(`/farmer/edit/${id}`)}>
            ✏️ Edit
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewFarmer;